/**
 * The till's on-device store (IndexedDB). No library: three small stores and a
 * handful of promise wrappers.
 *
 *  - `kv`:    the downloaded pack, the device id, the receipt counter
 *  - `queue`: sales and shift closes waiting to sync
 *  - `pins`:  who may unlock this till offline
 */
const DB_NAME = 'mlm-till-offline';
const DB_VERSION = 1;

let opening: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!opening) {
    opening = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
        if (!db.objectStoreNames.contains('queue'))
          db.createObjectStore('queue', { keyPath: 'id' });
        if (!db.objectStoreNames.contains('pins'))
          db.createObjectStore('pins', { keyPath: 'userId' });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        opening = null;
        reject(req.error);
      };
    });
  }
  return opening;
}

type StoreName = 'kv' | 'queue' | 'pins';

async function run<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  op: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = op(tx.objectStore(store));
    // Resolve on the transaction, not the request: a sale must really be on disk
    // before the till says it went through.
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export const kvGet = <T>(key: string) =>
  run<T | undefined>('kv', 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
export const kvSet = (key: string, value: unknown) =>
  run('kv', 'readwrite', (s) => s.put(value, key));
export const kvDelete = (key: string) => run('kv', 'readwrite', (s) => s.delete(key));

export const allRows = <T>(store: 'queue' | 'pins') =>
  run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
export const getRow = <T>(store: 'queue' | 'pins', id: string) =>
  run<T | undefined>(store, 'readonly', (s) => s.get(id) as IDBRequest<T | undefined>);
export const putRow = (store: 'queue' | 'pins', row: unknown) =>
  run(store, 'readwrite', (s) => s.put(row));
export const deleteRow = (store: 'queue' | 'pins', id: string) =>
  run(store, 'readwrite', (s) => s.delete(id));

/** The next number in a device-wide counter, durable across restarts. */
export async function nextCounter(key: string): Promise<number> {
  const db = await openDb();
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction('kv', 'readwrite');
    const store = tx.objectStore('kv');
    let next = 1;
    const get = store.get(key);
    get.onsuccess = () => {
      next = (typeof get.result === 'number' ? get.result : 0) + 1;
      store.put(next, key);
    };
    tx.oncomplete = () => resolve(next);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
